/**
 * The postMessage contract between the Welfare app and whichever host is
 * embedding it — the Military App web app (iframe) or the mobile app (WebView).
 *
 * This file is mirrored byte-for-byte in:
 *   - frontend/web/src/utils/handoffMessages.ts
 *   - frontend/mobile/app/utils/handoffMessages.ts
 *
 * Keep the three in step. They are duplicated rather than shared because the
 * three apps have no common package, and a silent drift here is the worst kind
 * of bug this feature can have: both sides keep working, the messages simply
 * stop being heard, and the symptom is a spinner that never clears with nothing
 * logged anywhere.
 */

/** Marks a message as ours. Anything without it is someone else's traffic. */
export const HANDOFF_SOURCE_WELFARE = 'welfare-app';
export const HANDOFF_SOURCE_HOST = 'military-app';

/** Welfare → host: "I have no usable session, send me a handoff token." */
export const WELFARE_AUTH_REQUIRED = 'WELFARE_AUTH_REQUIRED';

/** Host → Welfare: "here is one." */
export const WELFARE_HANDOFF_TOKEN = 'WELFARE_HANDOFF_TOKEN';

/** Host → Welfare: "I could not get you one." Ends the wait instead of hanging. */
export const WELFARE_HANDOFF_UNAVAILABLE = 'WELFARE_HANDOFF_UNAVAILABLE';

export type HandoffRequestMessage = {
    source: typeof HANDOFF_SOURCE_WELFARE;
    type: typeof WELFARE_AUTH_REQUIRED;
};

export type HandoffTokenMessage = {
    source: typeof HANDOFF_SOURCE_HOST;
    type: typeof WELFARE_HANDOFF_TOKEN;
    // eslint-disable-next-line camelcase
    handoff_token: string;
};

export type HandoffUnavailableMessage = {
    source: typeof HANDOFF_SOURCE_HOST;
    type: typeof WELFARE_HANDOFF_UNAVAILABLE;
    reason?: string;
};

export type HandoffHostMessage = HandoffTokenMessage | HandoffUnavailableMessage;

/**
 * Parse a `MessageEvent.data` into one of our host messages, or null.
 *
 * Tolerates both a string and an already-parsed object: an iframe parent posts
 * a structured clone, while the mobile WebView bridge can only carry a string.
 *
 * @param {unknown} data the raw message payload
 * @returns {HandoffHostMessage|null} the message, or null when it is not ours
 */
export const parseHostMessage = (data: unknown): HandoffHostMessage | null => {
    let payload: any = data;

    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload);
        } catch {
            return null;
        }
    }

    if (!payload || typeof payload !== 'object') return null;
    if (payload.source !== HANDOFF_SOURCE_HOST) return null;

    if (payload.type === WELFARE_HANDOFF_TOKEN && typeof payload.handoff_token === 'string') {
        return payload as HandoffTokenMessage;
    }

    if (payload.type === WELFARE_HANDOFF_UNAVAILABLE) {
        return payload as HandoffUnavailableMessage;
    }

    return null;
};

/**
 * Parse a message sent by the Welfare app, for hosts to use.
 *
 * @param {unknown} data the raw message payload
 * @returns {HandoffRequestMessage|null} the message, or null when it is not ours
 */
export const parseWelfareMessage = (data: unknown): HandoffRequestMessage | null => {
    let payload: any = data;

    if (typeof payload === 'string') {
        try {
            payload = JSON.parse(payload);
        } catch {
            return null;
        }
    }

    if (!payload || typeof payload !== 'object') return null;
    if (payload.source !== HANDOFF_SOURCE_WELFARE) return null;
    if (payload.type !== WELFARE_AUTH_REQUIRED) return null;

    return payload as HandoffRequestMessage;
};
